import { Fragment } from "react";
import { Box, Button, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Service } from "../../../types/service.type";
import { IdeallyFinishesInIcon, NumberOfVideosIcon, ProgramLevelIcon } from "../../../assets/icons/program-request";
import {
  SummaryHeader,
  SummaryInfo,
  SummaryInfoWithChips,
  SummaryProviderDetails,
  SummaryShare,
} from "../../../components/Summary";
import { useDeleteService } from "../../../api/mutations/useDeleteService";
import { useUpdateServiceStatus } from "../../../api/mutations/useUpdateServiceStatus";
import { CancelModal, useCancelModal } from "../../../components/CancelModal";
import useIsOwn from "../../../../../hooks/useIsOwn";
import { ServiceStatusEnum } from "../../../enum/service.enum";

const ProgramRequestSummary = ({ programService }: { programService?: Service }) => {
  const { t } = useTranslation("goService", { keyPrefix: "programRequestPage" });
  const { t: tProgramLevel } = useTranslation("goService", { keyPrefix: "programLevel" });
  const { t: tServicePage } = useTranslation("goService", { keyPrefix: "servicePage" });
  const {
    openCancelModal: openDeleteModal,
    handleOpenCancelModal: handleOpenDeleteModal,
    handleCloseCancelModal: handleCloseDeleteModal,
  } = useCancelModal();

  const { isOwn: isOwnProgram, isLoading: isOwnProgramLoading } = useIsOwn({
    userId: programService?.user?.id,
    isUserDataLoading: false,
  });

  const isLoading = isOwnProgramLoading;

  const canControlServiceStatus = isOwnProgram && programService?.approvedByAdmin;

  const { mutate: deleteService, isPending: isDeletingService } = useDeleteService(programService?.id!);

  const { mutate: updateServiceStatus, isPending: isUpdatingService } = useUpdateServiceStatus(programService?.id!);

  const handleToggleServiceStatus = () => {
    if (!programService?.status) return;
    const newStatus =
      programService.status === ServiceStatusEnum.Published ? ServiceStatusEnum.Draft : ServiceStatusEnum.Published;
    updateServiceStatus({ status: newStatus });
  };

  if (!programService) return null;

  const { user } = programService;
  const { currency, amount } = programService.paymentConfig;

  const handleRemoveService = () => {
    handleOpenDeleteModal();
  };

  const handleConfirmDeleteService = () => {
    deleteService();
    handleCloseDeleteModal();
  };

  const programDetails = [
    {
      icon: <IdeallyFinishesInIcon />,
      title: t("ideallyFinishesIn"),
      value: programService.idealCompletionTime,
    },
    {
      icon: <ProgramLevelIcon />,
      title: t("programLevel"),
      value: tProgramLevel(programService?.level ?? "beginner"),
    },
    {
      icon: <NumberOfVideosIcon />,
      title: t("numberOfVideos"),
      value: t("video", { count: programService.numberOfVideos }),
    },
  ];

  return (
    <Box sx={styles.container}>
      <SummaryHeader
        amount={amount}
        currency={currency}
        type={programService.type}
        expiryDate={programService.paymentConfig.expiryDate}
      />
      <Divider />
      <Box sx={styles.summaryDetails}>
        {programDetails.map(({ icon, title, value }) => (
          <Fragment key={title}>
            {Array.isArray(value) && (
              <SummaryInfoWithChips icon={icon} title={title} firstItemName={value[0]} totalLength={value.length} />
            )}
            {!Array.isArray(value) && <SummaryInfo icon={icon} title={title} value={value} />}
          </Fragment>
        ))}
      </Box>
      <Divider />
      <SummaryProviderDetails user={user!} isProvider={false} />
      <Divider />
      {canControlServiceStatus && programService?.status === ServiceStatusEnum.Published && (
        <Button onClick={handleToggleServiceStatus} disabled={isLoading || isUpdatingService} variant="outlined-md">
          {tServicePage("pauseThisService")}
        </Button>
      )}
      {canControlServiceStatus && programService?.status === ServiceStatusEnum.Draft && (
        <Button onClick={handleToggleServiceStatus} disabled={isLoading || isUpdatingService} variant="outlined-md">
          {tServicePage("publishThisService")}
        </Button>
      )}
      {isOwnProgram && (
        <Button
          onClick={handleRemoveService}
          disabled={isLoading || isDeletingService}
          variant="outlined-md"
          sx={styles.removeServiceButton}
        >
          {t("removeThisProgram")}
        </Button>
      )}
      <SummaryShare title={t("shareThisProgram")} />

      <CancelModal
        open={openDeleteModal}
        closeModal={handleCloseDeleteModal}
        handelCancel={handleConfirmDeleteService}
        title={tServicePage("areYouSureToRemoveThisService")}
      />
    </Box>
  );
};

export default ProgramRequestSummary;

const styles = {
  container: {
    position: "sticky",
    top: "10px",
    display: "flex",
    width: { xs: "100%", md: "300px" },
    padding: "24px 16px",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
    background: "var(--Gray-White, #FFF)",
    height: "fit-content",
  },
  summaryDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  removeServiceButton: {
    backgroundColor: "#FFF",
    color: "var(--Red---200, #CC3E3E)",
    border: "1px solid var(--Red---200, #CC3E3E)",
  },
} as const;
